import {getFormattedDateTime} from "@ots-frontend/ots-data";
import {PersonEntity} from "@ots-frontend/ots-data";
import {toDisplayText} from "../../utils/localized-text";

/** Only what the profile shows of a role, so hosts can pass ots-data entities or plain GWT values. */
export type UserProfileRole = {
  id: string;
  name: string;
  description?: string;
};

/** A team the person belongs to, with their own membership dates. */
export type UserProfileTeam = {
  id: string;
  name: string;
  description?: string;
  /** What the team covers, e.g. a region or company. */
  scopeName?: string;
  parentTeamName?: string;
  /** False when the membership, or the team itself, is switched off. */
  enabled?: boolean;
  effectiveStartDttm?: number;
  effectiveEndDttm?: number;
};

/**
 * Work the person is set up to do -- Driver, Dispatcher, Field Technician. Distinct from roles, which
 * decide what they may access; a capability decides what they can be assigned to.
 */
export type UserProfileCapability = {
  id: string;
  name: string;
  /** The one they mainly do, when they have several. */
  primary?: boolean;
};

/** The primary capability leads; the rest follow by name. */
export const sortCapabilitiesForProfile = (capabilities: UserProfileCapability[]): UserProfileCapability[] =>
  [...capabilities].sort((a, b) => Number(Boolean(b.primary)) - Number(Boolean(a.primary)) || a.name.localeCompare(b.name));

export type TeamMembershipStatus = "active" | "upcoming" | "ended" | "inactive";

export const getTeamMembershipStatus = (team: UserProfileTeam, now: number = Date.now()): TeamMembershipStatus => {
  if (team.enabled === false) {
    return "inactive";
  }
  if (team.effectiveStartDttm && team.effectiveStartDttm > now) {
    return "upcoming";
  }
  if (team.effectiveEndDttm && team.effectiveEndDttm <= now) {
    return "ended";
  }
  return "active";
};

const teamStatusOrder: Record<TeamMembershipStatus, number> = {active: 0, upcoming: 1, ended: 2, inactive: 3};

/** Current teams first, then upcoming, then past and switched-off ones; by name within each. */
export const sortTeamsForProfile = (teams: UserProfileTeam[], now: number = Date.now()): UserProfileTeam[] =>
  [...teams].sort((a, b) =>
    teamStatusOrder[getTeamMembershipStatus(a, now)] - teamStatusOrder[getTeamMembershipStatus(b, now)]
    || a.name.localeCompare(b.name));

export const getPreferredLanguageLabel = (person: PersonEntity): string | undefined =>
  toDisplayText(person.preferredLanguage?.displayValue);

/** Region names when the host has them; otherwise a count, since raw ids mean nothing to a reader. */
export const getRegionsLabel = (person: PersonEntity, regionNames?: string[]): string | undefined => {
  if (regionNames?.length) {
    return [...regionNames].sort((a, b) => a.localeCompare(b)).join(", ");
  }

  const count = person.regionIds?.length ?? 0;
  return count ? `${count} region${count === 1 ? "" : "s"}` : undefined;
};

export const sortRolesByName = <T extends {name: string}>(roles: T[]): T[] =>
  [...roles].sort((a, b) => a.name.localeCompare(b.name));

export const formatProfileDate = (epochMillis?: number): string | undefined =>
  epochMillis == null ? undefined : getFormattedDateTime(epochMillis, false);
